/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 *
 * Env Drift board: fleet-wide promotion posture between two envs
 * (staging->prod by default; a service with no values for the source env is
 * compared from the nearest lower env it has, and the row says which).
 * Intent columns come from git (image.tag per env values file); when the
 * backend has ArgoCD tokens configured, live sync/health decorate each row
 * and mismatches between git intent and the cluster surface as flags.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Content,
  Header,
  Page,
  Progress,
  StatusError,
  StatusOK,
  StatusPending,
  StatusRunning,
  StatusWarning,
  Table,
  TableColumn,
  WarningPanel,
} from '@backstage/core-components';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import Link from '@material-ui/core/Link';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import RefreshIcon from '@material-ui/icons/Refresh';
import { HowItWorks } from '../common/HowItWorks';
import { BoardRow, DriftFlag, DriftStatus, EnvDriftBoard } from './types';
import { useEnvDriftApi } from './useEnvDriftApi';

const useStyles = makeStyles(theme => ({
  tiles: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  tile: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: theme.spacing(1.5),
    cursor: 'pointer',
    background: theme.palette.background.paper,
    '&[data-active="true"]': {
      borderColor: theme.palette.primary.main,
      boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
    },
  },
  tileCount: {
    fontSize: 28,
    fontWeight: 600,
    lineHeight: 1.1,
    fontVariantNumeric: 'tabular-nums',
  },
  tag: {
    fontFamily: 'monospace',
    fontSize: 12,
    whiteSpace: 'nowrap',
  },
  liveTag: {
    fontFamily: 'monospace',
    fontSize: 11,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  // Argo health coloring for the live line (defined after liveTag so the
  // color wins at equal specificity).
  liveOk: { color: theme.palette.success.main },
  liveError: { color: theme.palette.error.main, fontWeight: 600 },
  liveRunning: { color: theme.palette.info.main },
  liveMuted: { color: theme.palette.text.secondary },
  meta: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
}));

// Canonical left-to-right promotion order for the all-envs view; anything
// unknown (future envs) sorts after these.
const ENV_ORDER = [
  'platform-ci',
  'platform-gamedev',
  'platform-staging',
  'platform-prod',
];
const orderEnvs = (envs: string[]): string[] =>
  [...envs].sort((a, b) => {
    const ia = ENV_ORDER.indexOf(a);
    const ib = ENV_ORDER.indexOf(b);
    return (ia === -1 ? ENV_ORDER.length : ia) - (ib === -1 ? ENV_ORDER.length : ib);
  });

const envShort = (env: string) => env.replace(/^platform-/, '');
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

type ComparePair = { from: string; to: string };

// Labels name the envs actually compared (board pair, or the env a row fell
// back to), so a staging → prod board never reads "Gamedev only".
const statusLabels = (cmp: ComparePair): Record<DriftStatus, string> => ({
  drift: `${cap(envShort(cmp.to))} behind`,
  'never-promoted': 'Never promoted',
  'from-only': `${cap(envShort(cmp.from))} only`,
  'to-only': `${cap(envShort(cmp.to))} only`,
  'in-sync': 'In sync',
  'no-image-pin': 'Not pinned',
});

// Severity order for the Status column sort (worst first). Without this the
// header click sorts the raw strings alphabetically, which interleaves
// green between red and yellow ('in-sync' < 'never-promoted').
const STATUS_RANK: Record<DriftStatus, number> = {
  drift: 0,
  'never-promoted': 1,
  'from-only': 2,
  'to-only': 3,
  'in-sync': 4,
  'no-image-pin': 5,
};

const flagLabels = (cmp: ComparePair): Record<DriftFlag, string> => ({
  'to-env-scaled-to-zero': `${envShort(cmp.to)} ×0`,
  'from-env-scaled-to-zero': `${envShort(cmp.from)} ×0`,
  'live-mismatch': 'live ≠ intent',
  'out-of-sync': 'OutOfSync',
  unhealthy: 'unhealthy',
});

// The pair a row was really classified against: the board's compare.to, and
// the env the backend fell back to when the service has no compare.from.
const rowPair = (row: BoardRow, compare: ComparePair): ComparePair => ({
  from: row.fromEnv,
  to: compare.to,
});

const StatusIndicator = ({
  row,
  compare,
  classes,
}: {
  row: BoardRow;
  compare: ComparePair;
  classes: ReturnType<typeof useStyles>;
}) => {
  const label = statusLabels(rowPair(row, compare))[row.status];
  let indicator: React.ReactElement;
  switch (row.status) {
    case 'in-sync':
      indicator = <StatusOK>{label}</StatusOK>;
      break;
    case 'drift':
      indicator = <StatusError>{label}</StatusError>;
      break;
    case 'never-promoted':
      indicator = <StatusWarning>{label}</StatusWarning>;
      break;
    case 'from-only':
    case 'to-only':
      indicator = <StatusRunning>{label}</StatusRunning>;
      break;
    default:
      indicator = <StatusPending>{label}</StatusPending>;
  }
  return (
    <Box>
      {indicator}
      {row.fromEnv !== compare.from && (
        <Typography component="div" className={classes.meta}>
          vs {envShort(row.fromEnv)} · no {envShort(compare.from)}
        </Typography>
      )}
    </Box>
  );
};

const EnvTagCell = ({
  row,
  env,
  classes,
}: {
  row: BoardRow;
  env: string;
  classes: ReturnType<typeof useStyles>;
}) => {
  const cell = row.envs[env];
  if (!cell) return <span>—</span>;
  const zero = cell.replicaCount === 0 ? ' ×0' : '';
  const argo = cell.argo;
  const liveDiffers =
    cell.liveTag != null && cell.tag !== null && cell.liveTag !== cell.tag;
  const healthClass = !argo
    ? classes.liveMuted
    : argo.health === 'Healthy'
      ? classes.liveOk
      : argo.health === 'Degraded'
        ? classes.liveError
        : argo.health === 'Progressing'
          ? classes.liveRunning
          : classes.liveMuted;
  return (
    <Box>
      <span className={classes.tag}>
        {cell.tag ?? '(no image pin)'}
        {zero}
      </span>
      {argo && (
        <Tooltip
          title={`ArgoCD: ${argo.syncStatus} / ${argo.health}${
            argo.lastSyncedAt ? ` — last sync ${argo.lastSyncedAt}` : ''
          }`}
        >
          <Box className={`${classes.liveTag} ${healthClass}`}>
            {'● '}
            {liveDiffers ? `live ${cell.liveTag}` : 'live ✓'}
            {argo.health !== 'Healthy' ? ` · ${argo.health}` : ''}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

export const EnvDriftPage = () => {
  const classes = useStyles();
  const api = useEnvDriftApi();
  const [board, setBoard] = useState<EnvDriftBoard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<DriftStatus | null>(null);
  // '' = unset. A comparison is active only when BOTH sides are chosen; until
  // then the board is the plain all-envs overview (no pair-relative UI).
  const [fromSel, setFromSel] = useState('');
  const [toSel, setToSel] = useState('');
  const pair = useMemo(
    () => (fromSel && toSel ? { from: fromSel, to: toSel } : null),
    [fromSel, toSel],
  );

  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .getBoard(pair?.from, pair?.to)
      .then(b => {
        if (active) {
          setBoard(b);
          setError(null);
        }
      })
      .catch(e => active && setError(String(e.message ?? e)))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [api, pair]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      setBoard(await api.refreshBoard(pair?.from, pair?.to));
      setError(null);
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setRefreshing(false);
    }
  }, [api, pair]);

  const setPairSide = useCallback(
    (side: 'from' | 'to', value: string) => {
      if (side === 'from') {
        setFromSel(value);
        // picking the env already on the other side swaps them
        if (value && value === toSel) setToSel(fromSel);
      } else {
        setToSel(value);
        if (value && value === fromSel) setFromSel(toSel);
      }
      setStatusFilter(null);
    },
    [fromSel, toSel],
  );

  const clearPair = useCallback(() => {
    setFromSel('');
    setToSel('');
    setStatusFilter(null);
  }, []);

  const rows = useMemo(() => {
    if (!board) return [];
    return statusFilter
      ? board.rows.filter(r => r.status === statusFilter)
      : board.rows;
  }, [board, statusFilter]);

  const columns: TableColumn<BoardRow>[] = useMemo(() => {
    if (!board) return [];
    // Pair view: the compared envs plus any env a row fell back to, so the
    // tag a drift row was compared from is on screen. All-envs view: ladder.
    const visibleEnvs = pair
      ? orderEnvs(
          Array.from(
            new Set([pair.from, pair.to, ...board.rows.map(r => r.fromEnv)]),
          ),
        )
      : orderEnvs(board.envs);
    const envCols: TableColumn<BoardRow>[] = visibleEnvs.map(env => ({
      title: env.replace(/^platform-/, ''),
      field: `envs.${env}.tag`,
      sorting: false,
      render: row => <EnvTagCell row={row} env={env} classes={classes} />,
    }));
    const statusCol: TableColumn<BoardRow> = {
      title: 'Status',
      field: 'status',
      width: '140px',
      customSort: (a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status],
      render: row => (
        <StatusIndicator row={row} compare={board.compare} classes={classes} />
      ),
    };
    return [
      // Status and Flags are pair-relative concepts — hidden in the
      // no-comparison overview.
      ...(pair ? [statusCol] : []),
      {
        title: 'Service',
        field: 'name',
        highlight: true,
        render: row => (
          <Box>
            <Link
              href={`https://github.com/${row.repo.owner}/${row.repo.name}`}
              target="_blank"
              rel="noopener"
            >
              {row.name}
            </Link>
            {row.team && (
              <Typography component="div" className={classes.meta}>
                {row.team}
              </Typography>
            )}
          </Box>
        ),
      },
      ...envCols,
      ...(pair
        ? ([
      {
        title: 'Flags',
        field: 'flags',
        sorting: false,
        render: row => (
          <Box display="flex" style={{ gap: 4, flexWrap: 'wrap' }}>
            {row.flags.map(f => (
              <Chip
                key={f}
                size="small"
                label={flagLabels(rowPair(row, board.compare))[f]}
                color={
                  f === 'live-mismatch' || f === 'unhealthy'
                    ? 'secondary'
                    : 'default'
                }
              />
            ))}
            {row.fetchError && (
              <Tooltip title={row.fetchError}>
                <Chip size="small" label="fetch error" color="secondary" />
              </Tooltip>
            )}
          </Box>
        ),
      },
        ] as TableColumn<BoardRow>[])
        : []),
      {
        title: `${board.compare.to.replace(/^platform-/, '')} last promoted`,
        field: 'toEnvLastPromotedAt',
        type: 'date',
        render: row =>
          row.toEnvLastPromotedAt ? (
            <span className={classes.meta}>
              {new Date(row.toEnvLastPromotedAt).toISOString().slice(0, 10)}
            </span>
          ) : (
            <span className={classes.meta}>—</span>
          ),
      },
    ];
  }, [board, classes, pair]);

  const tiles: DriftStatus[] = [
    'drift',
    'never-promoted',
    'from-only',
    'in-sync',
    'no-image-pin',
  ];

  const ladder = orderEnvs(board?.envs ?? ENV_ORDER)
    .map(envShort)
    .join(' → ');

  return (
    <Page themeId="tool">
      <Header
        title="Env Drift"
        subtitle={
          board
            ? `${
                pair ? `comparing ${pair.from} → ${pair.to} · ` : ''
              }generated ${new Date(board.generatedAt).toLocaleString()} · ${
                board.hasLiveState
                  ? 'git intent + ArgoCD live state'
                  : 'git intent only (no ArgoCD tokens configured)'
              }`
            : 'What each environment should be running vs what it is running'
        }
      />
      <Content>
        <HowItWorks
          collapseKey="envDrift.howItWorks"
          lead={
            <>
              Every environment's deployment intent is pinned in git:{' '}
              <code>gitops/argocd/env/&lt;env&gt;/values.yaml</code> sets{' '}
              <code>image.tag</code> — the one field a promotion changes. The
              board scans those files for every catalog service (every 30
              minutes; the refresh action re-scans on demand) and merges in
              what each environment's ArgoCD reports is actually running, so
              intent and reality sit side by side.
            </>
          }
          steps={[
            {
              title: 'Overview',
              body: (
                <>
                  All environments in promotion order ({ladder}). Each cell
                  shows the pinned tag plus a colored ArgoCD live line — green ✓
                  healthy, red Degraded, blue Progressing. ×0 means replicas are
                  deliberately pinned to zero; “(no image pin)” means that env's
                  values file pins no image (normal for chart-versioned services
                  like trino/superset).
                </>
              ),
            },
            {
              title: 'Compare',
              body: (
                <>
                  Pick two environments to classify every service for that
                  pair: <b>Prod behind</b> (target trails source),{' '}
                  <b>Never promoted</b> (target still pins a scaffold tag or{' '}
                  <code>latest</code>), <b>In sync</b>, <b>Not pinned</b>.
                  Tiles summarize and filter; “last promoted” is the git-blame
                  date of the target env's <code>image.tag</code> line — when
                  the pin actually moved, not when the file was last touched. A
                  service with no values for the source env is compared from the
                  nearest lower env it has; the Status cell names that env and
                  its column is shown.
                </>
              ),
            },
            {
              title: 'Flags',
              body: (
                <>
                  <b>live ≠ intent</b> — ArgoCD is running a different build
                  than git pins (stuck sync or manual override).{' '}
                  <b>OutOfSync</b> / <b>unhealthy</b> come straight from
                  ArgoCD. <b>fetch error</b> — that repo's values couldn't be
                  read or parsed (details on hover). Flags are relative to the
                  selected pair.
                </>
              ),
            },
          ]}
          footer={
            <Typography variant="caption" color="textSecondary">
              Sources: GitHub (values on each repo's default branch) ·
              argo.&#123;ci,gamedev,prod&#125;.mission42.com · rebuilds every
              30 min · refresh re-scans immediately
            </Typography>
          }
        />
        {loading && <Progress />}
        {error && (
          <WarningPanel severity="error" title="Could not load the drift board">
            {error}
          </WarningPanel>
        )}
        {board && (
          <>
            <Box
              display="flex"
              alignItems="center"
              style={{ gap: 12, marginBottom: 14 }}
            >
              <Typography variant="body2" color="textSecondary">
                Compare
              </Typography>
              <TextField
                select
                size="small"
                variant="outlined"
                value={fromSel}
                onChange={e => setPairSide('from', e.target.value)}
                SelectProps={{ displayEmpty: true }}
                inputProps={{ 'aria-label': 'compare from environment' }}
              >
                <MenuItem value="">
                  <em>env…</em>
                </MenuItem>
                {orderEnvs(board.envs).map(e => (
                  <MenuItem key={e} value={e}>
                    {e.replace(/^platform-/, '')}
                  </MenuItem>
                ))}
              </TextField>
              <Typography variant="body2" color="textSecondary">
                →
              </Typography>
              <TextField
                select
                size="small"
                variant="outlined"
                value={toSel}
                onChange={e => setPairSide('to', e.target.value)}
                SelectProps={{ displayEmpty: true }}
                inputProps={{ 'aria-label': 'compare to environment' }}
              >
                <MenuItem value="">
                  <em>env…</em>
                </MenuItem>
                {orderEnvs(board.envs).map(e => (
                  <MenuItem key={e} value={e}>
                    {e.replace(/^platform-/, '')}
                  </MenuItem>
                ))}
              </TextField>
              {pair ? (
                <Chip
                  size="small"
                  label="Clear comparison"
                  onClick={clearPair}
                  onDelete={clearPair}
                />
              ) : (
                <Typography variant="caption" color="textSecondary">
                  Showing all environments — choose two to compare.
                </Typography>
              )}
            </Box>
            {pair && (
            <Box className={classes.tiles}>
              {tiles.map(s => (
                <Box
                  key={s}
                  className={classes.tile}
                  data-active={statusFilter === s}
                  onClick={() =>
                    setStatusFilter(cur => (cur === s ? null : s))
                  }
                  role="button"
                  tabIndex={0}
                  onKeyDown={e =>
                    (e.key === 'Enter' || e.key === ' ') &&
                    setStatusFilter(cur => (cur === s ? null : s))
                  }
                >
                  <Typography className={classes.tileCount}>
                    {board.summary[s] ?? 0}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {statusLabels(board.compare)[s]}
                  </Typography>
                </Box>
              ))}
            </Box>
            )}
            <Table<BoardRow>
              title={`Services (${rows.length}${
                statusFilter
                  ? ` · ${statusLabels(board.compare)[statusFilter]}`
                  : ''
              })`}
              options={{
                paging: false,
                padding: 'dense',
                search: true,
                thirdSortClick: false,
              }}
              columns={columns}
              data={rows}
              actions={[
                {
                  icon: () => <RefreshIcon />,
                  tooltip: refreshing
                    ? 'Refreshing…'
                    : 'Re-scan GitHub + ArgoCD now',
                  isFreeAction: true,
                  disabled: refreshing,
                  onClick: onRefresh,
                },
              ]}
              emptyContent={
                <Box p={2}>
                  <Typography>
                    No services matched. The board excludes repos with no
                    ArgoCD env overlays.
                  </Typography>
                </Box>
              }
            />
            {refreshing && <Progress />}
            {!board.hasLiveState && (
              <Box mt={1}>
                <Typography variant="caption" color="textSecondary">
                  Live ArgoCD columns appear once read tokens are configured
                  (m42.envDrift.argocd.instances).
                </Typography>
              </Box>
            )}
          </>
        )}
      </Content>
    </Page>
  );
};
